import {ReactiveController, ReactiveControllerHost} from "lit";
import {FormFieldConfiguration} from "@ha/formhandler/types/form-field-configuration";
import {IValidator} from "@ha/formhandler/types/i-validator";
import {bindTo, BindToDirective, ComplexFormControl, FormBuilder, FormControlNotification} from "@ha/formhandler";
import {filter, Observable, Subject, takeUntil} from "rxjs";
import {DirectiveResult} from "lit/directive.js";

export class FormController<T extends Record<string, unknown>> implements ReactiveController {

    private _disconnected$ = new Subject<void>();

    constructor(
        private _host: ReactiveControllerHost,
        private _key: string,
        private _settings: FormFieldConfiguration[],
        private _validations?: IValidator[],
        private _payload?: unknown
    ) {
        _host.addController(this);
    }

    private _form!: ComplexFormControl<T, unknown>;

    public get form(): ComplexFormControl<T, unknown> | null {
        return this._form;
    }

    /**
     * Gets an observable that emits form control notifications.
     *
     * @return {Observable<FormControlNotification>} An observable stream of form control notifications.
     */
    public get event$(): Observable<FormControlNotification> {
        return this._form.event$;
    }

    public get isSavable(): boolean {
        return this._form?.isValid && this._form?.isDirty || false;
    }

    public get isDirty(): boolean {
        return this._form?.isDirty || false;
    }

    public get isValid(): boolean {
        return this._form?.isValid || false;
    }

    /**
     * Gibt eine Funktion zurück, die die bindTo-Direktive für ein bestimmtes Feld erzeugt
     */
    public get bindToFn(): (fieldPath: string) => DirectiveResult<typeof BindToDirective> {
        return this.bindTo.bind(this);
    }

    public hostConnected(): void {
        this._initializeForm().then();
    }

    public hostDisconnected() {
        this._disconnected$.next();
        this._disconnected$.complete();
    }

    /**
     * Setzt das Formular zurück
     */
    public reset(): void {
        this._form?.reset();
        this._host.requestUpdate();
    }

    /**
     * Übernimmt die aktuellen Werte als neue Initialwerte
     */
    public commit(): void {
        this._form?.commit();
    }

    /**
     * Erzeugt eine bindTo-Direktive für ein bestimmtes Feld
     */
    protected bindTo(fieldPath: string): DirectiveResult<typeof BindToDirective> {
        if (!this._form) {
            throw new Error('Formular nicht initialisiert');
        }

        return bindTo(this._form, fieldPath);
    }

    private async _initializeForm(): Promise<void> {
        this._form = FormBuilder.buildForm(
            this._key,
            this._settings,
            this._validations,
            this._payload
        );
        // Auf Formularänderungen reagieren
        this._form.event$.pipe(
            takeUntil(this._disconnected$),
            filter((notification: FormControlNotification) => !notification.event.startsWith(`validation.`))
        ).subscribe(notification => {
            const [, ...subpath] = notification.formControl!.path.split(`.`);
            const path = subpath.join(`.`);
            this._host.requestUpdate();
        });
    }
}